import { useContext, useEffect, useState } from 'react';

import {
	BookOutlined,
	DeleteOutlined,
	DownloadOutlined,
	InfoCircleOutlined,
	InfoOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Flex,
	Input,
	Space,
	Spin,
	Table,
	Tooltip,
	message,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	downloadBatchJsonData,
	useArchiveProcess,
	useDeleteBatchProcess,
	useGetAllCustomerProcesses,
	useReprocessBatchProcess,
} from '@/api/batch_process';
import BatchMultiButton from '@/components/common/BatchMultiButton';
import ViewBatchProcessReportDrawer from '@/components/drawers/ViewBatchProcessReportDrawer';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import { ClientExtendedSubscription } from '@/layouts/subscription/subscriptionType';
import { useSubscription } from '@/layouts/subscription/useSubscription';
import downloadZipFile from '@/lib/helpers/downloadZipFile';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import { PageContext } from '@/providers/PageContextProvider';

import { columns } from './data';

export interface IPagination {
	page: number;
	pageCount: number;
	total: number | undefined;
	limit: number;
}

export default function VideoIntelligenceOnDemand() {
	const navigate = useNavigate();
	const { setPageTitle, openDrawer, setIsCreditsCounterVisible } =
		useContext(PageContext);
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { isLoading: isSubscriptionLoading } = useSubscription();
	const [jsonDataLoading, setJsonDataLoading] = useState({
		isLoading: false,
		id: '',
	});
	const [isRefreshEnabled, setIsRefreshEnabled] = useState(false);
	const [currentId, setCurrentId] = useState<string | null>(null);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});
	const [sortBy, setSortBy] = useState<string>('started');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
	const [status, setStatus] = useState<any>('');

	const [search, setSearch] = useState('');

	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isFetching, refetch } = useGetAllCustomerProcesses(
		{
			page: pagination.page,
			limit: pagination.limit,
			sortBy,
			sortOrder,
			status,
			search: debouncedSearch.length >= 3 ? debouncedSearch : '',
		},
		isRefreshEnabled,
	);

	const { mutate: reprocessBatch, isLoading: isReprocessLoading } =
		useReprocessBatchProcess();
	const { mutate: deleteBatchProcess, isLoading: isDeleteLoading } =
		useDeleteBatchProcess();
	const { mutate: archiveProcess, isLoading: isArchiveLoading } =
		useArchiveProcess();

	const total = data?.data?.data?.pagination?.total ?? 1;
	const batchItems = data?.data?.data?.items ?? [];

	useEffect(() => {
		if (batchItems.length > 0) {
			if (batchItems?.some((item: any) => item?.status === 'processing')) {
				setIsRefreshEnabled(true);
			}
		}
	}, [batchItems]);

	useEffect(() => {
		setPageTitle('Video intelligence on-demand');
		setIsCreditsCounterVisible(false);

		return () => {
			setPageTitle('');
			setIsCreditsCounterVisible(true);
		};
	}, []);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.vod)) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, filters: any, sorter: any) => {
		const { field = 'started', order = 'descend' } = sorter;
		const curStatus = filters.status?.[0] ?? '';

		setSortBy(field);
		setSortOrder(order === 'ascend' ? 'asc' : 'desc');

		if (curStatus !== status) {
			setStatus(curStatus);
		} else if (!curStatus && status) {
			setStatus('');
		}

		// setSearchParams((searchParams) => {
		//   searchParams.set("status", curStatus || "");
		//   searchParams.set("type", type || "");
		//   searchParams.set("filters", platform || "");

		//   field && searchParams.set("sortBy", field);
		//   order &&
		// 	searchParams.set("sortOrder", order === "ascend" ? "asc" : "desc");

		//   return searchParams;
		// });
	};

	const onDownloadClick = async (record: any) => {
		try {
			setJsonDataLoading({
				isLoading: true,
				id: record?.batch_id,
			});

			const response = await downloadBatchJsonData(record?.batch_id);

			if (response.status) {
				downloadZipFile(record?.batch_id, response?.data);
			}
		} catch (error) {
			message.error(`Something went wrong: ${error}`);
		} finally {
			setJsonDataLoading({ isLoading: false, id: '' });
		}
	};

	const onReprocessClick = (record: any) => {
		setCurrentId(record?.batch_id);
		reprocessBatch(record?.batch_id, {
			onSuccess: () => {
				message.success('Reprocess started');
				refetch();
				setCurrentId(null);
			},
			onError: (error: any) => {
				message.error(`Something went wrong: ${error}`);
			},
		});
	};

	const onArchiveClick = (record: any) => {
		setCurrentId(record?.batch_id);
		archiveProcess(record?.batch_id, {
			onSuccess: () => {
				message.success('Archived');
				// refetch();
				setCurrentId(null);
			},
			onError: (error: any) => {
				message.error(`Something went wrong: ${error}`);
			},
		});
	};

	const onDeleteClick = (id: string) => {
		setCurrentId(id);

		deleteBatchProcess(id, {
			onSuccess: () => {
				message.success('Deleted');
				// refetch();
				setCurrentId(null);
			},
			onError: (error: any) => {
				message.error(`Something went wrong: ${error}`);
			},
		});
	};

	return (
		<>
			{isSubscriptionLoading ? (
				<Flex
					align="center"
					justify="center"
					style={{ width: '100%', paddingTop: '5rem' }}
				>
					<Spin size="large" />
				</Flex>
			) : (
				<div>
					<Flex
						align="center"
						justify="space-between"
						style={{ marginBottom: '0.5rem' }}
					>
						<h2>Video on demand - batch process history</h2>
					</Flex>

					<Flex gap={16} style={{ marginBottom: '1rem' }}>
						<Input
							type="text"
							placeholder="Search..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							size="small"
						/>

						<Space>
							<Tooltip title="Refresh">
								<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
							</Tooltip>

							<Button
								type="primary"
								onClick={() => openDrawer('add-new-batch-process')}
							>
								New
							</Button>
						</Space>
					</Flex>

					<Table
						loading={isLoading || isFetching}
						style={{ width: '100%' }}
						// onChange={onTableChange}
						dataSource={batchItems}
						// dataSource={mockData}
						columns={[
							...columns,
							{
								title: 'Actions',
								dataIndex: 'actions',
								key: 'actions',
								width: 70,
								// flex: true,
								align: 'center',
								render: (_: any, record: any) => {
									return (
										<BatchMultiButton
											record={record}
											onReprocessClick={onReprocessClick}
											onDownloadClick={onDownloadClick}
											onArchiveClick={onArchiveClick}
											isLoading={
												(isReprocessLoading &&
													currentId === record?.batch_id) ||
												(isDeleteLoading && currentId === record?.batch_id) ||
												(jsonDataLoading.isLoading &&
													jsonDataLoading.id === record?.batch_id) ||
												(isArchiveLoading && currentId === record?.batch_id)
											}
										/>
									);
								},
							},
						]}
						rowKey={'id'}
						bordered
						scroll={{ x: 1100 }}
						onChange={onTableChange}
						pagination={{
							total,
							current: pagination.page,
							showSizeChanger: true,
							pageSizeOptions: [10, 25, 50],
							onChange: (page: number, pageSize: number) => {
								setPagination((cur) => ({ ...cur, page, limit: pageSize }));
							},
						}}
					/>
				</div>
			)}
			<ViewBatchProcessReportDrawer />
		</>
	);
}
